import { FormEvent, useState, useEffect } from "react";
import {
  AlignItems,
  Box,
  ColorPreset,
  FontWeight,
  Form,
  Glyph,
  H2,
  Icon,
  Interpose,
  JustifyContent,
  P,
  PlainButton,
  ReadingOptimized,
  Space,
  Text,
  TypePreset,
  Visibility,
} from "@gocardless/flux-react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans } from "@lingui/macro";

import { ButtonFeedbackEnum, SignupFormData, ValidationMode } from "../consts";

import { VerificationCodeField } from "./VerificationCodeField/VerificationCodeField";
import { FeedbackButton } from "./FeedbackButton/FeedbackButton";

import { useSegment } from "src/technical-integrations/segment/useSegment";
import { TrackingEvent } from "src/common/trackingEvents";
import { SectionSeparator } from "src/components/layout";
import { SeparatorType } from "src/components/layout/types";

export interface EmailVerificationForm {
  verificationCode?: string;
  organisationName?: string;
}

export interface EmailVerificationProps {
  formData?: SignupFormData;
  isSubmittingRegistration: boolean;
  setShowSignupForm(showSignupForm: boolean): void;
  onSubmit: (verificationCode: string) => void;
  resendCode: (formData: SignupFormData) => void;
}

export const EmailVerification = ({
  formData,
  setShowSignupForm,
  onSubmit,
  isSubmittingRegistration,
  resendCode,
}: EmailVerificationProps) => {
  const { sendEvent } = useSegment();

  const formMethods = useForm<EmailVerificationForm>({
    defaultValues: {
      verificationCode: "",
      organisationName: formData?.business_name,
    },
    mode: ValidationMode.OnBlur,
    reValidateMode: ValidationMode.OnChange,
  });

  const [verificationCode, setVerificationCode] = useState<string>();

  const resetForm = () => {
    setVerificationCode("");
    formMethods.reset({ verificationCode: "" }, { keepDefaultValues: true });
  };

  const isValidCode = verificationCode?.length === 6;

  useEffect(() => {
    sendEvent(TrackingEvent.SIGNUP_EMAIL_VERIFICATION_PAGE_VIEWED);
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      layout="flex"
      justifyContent={JustifyContent.Center}
      alignItems={[AlignItems.Start, null, null, AlignItems.Center]}
      height="100%"
    >
      <Box
        width={["100%", null, "500px"]}
        spaceAbove={[6, null, null, 0]}
        spaceBelow={[4, null, null, 0]}
      >
        <Text weight={FontWeight.SemiBold} preset={TypePreset.Body_02}>
          <PlainButton
            onClick={() => {
              resetForm();
              setShowSignupForm(true);
              sendEvent(TrackingEvent.SIGNUP_BACK_BUTTON_CLICKED);
            }}
            spaceBelow={4}
          >
            <Icon name={Glyph.ChevronLeft} size="12px" />
            <Space h={0.5} layout="inline" />
            <Trans id="back">Back</Trans>
          </PlainButton>
        </Text>
        <ReadingOptimized alignText={AlignItems.FlexStart} textWidth="100%">
          <H2 spaceBelow={2} size={6}>
            <Trans id="optimised-signup.email-verification.heading">
              Check your email for a code
            </Trans>
          </H2>
          <FormProvider {...formMethods}>
            <Form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                verificationCode && onSubmit(verificationCode);
              }}
            >
              <Interpose node={<Space v={1.5} />}>
                <VerificationCodeField
                  verificationCode={verificationCode}
                  setVerificationCode={setVerificationCode}
                />
                <FeedbackButton
                  buttonFeedback={
                    isSubmittingRegistration
                      ? ButtonFeedbackEnum.IsLoading
                      : ButtonFeedbackEnum.Default
                  }
                  disabled={!isValidCode}
                />
              </Interpose>
            </Form>
          </FormProvider>
          <Box spaceAbove={[2, null, 4]}>
            <Visibility visible={["none", null, "block"]}>
              <SectionSeparator type={SeparatorType.DEFAULT} />
            </Visibility>
            <P preset={TypePreset.Body_01} color={ColorPreset.TextOnLight_02}>
              <Trans id="optimised-signup.email-verification.footer-text">
                Didn’t receive a code? Check your spam folder or
              </Trans>{" "}
              <PlainButton
                layout="inline"
                textDecoration="underline"
                onClick={() => {
                  resetForm();
                  formData && resendCode(formData);
                  sendEvent(TrackingEvent.SIGNUP_RESEND_CODE_CLICKED);
                }}
              >
                <Trans id="optimised-signup.email-verification.resend-code">
                  click to resend
                </Trans>
              </PlainButton>
            </P>
          </Box>
        </ReadingOptimized>
      </Box>
    </Box>
  );
};
